import React, { useRef, useEffect, useState } from "react";
import Anime from "react-anime";
import { useInView } from "react-intersection-observer";
import { animeSingleProps } from "@helpers/animations";
import { Heading } from "@components/Heading/Heading";
import { Container } from "@components/Container/Container";
import { ImageField } from "@helpers/interfaces/image";
import { UmbracoImage } from "@components/Image/UmbracoImage/UmbracoImage";
import { UmbracoBackgroundImage } from "@components/Image/UmbracoBackgroundImage/UmbracoBackgroundImage";
import styles from "@modules/MediaComparisonSlider/styles.module.scss";
import stylesRichtext from "@modules/Richtext/styles.module.scss";
import { Box } from "@components/Box/Box";

interface MediaComparisonSliderProps {
  data: {
    LeftVideo?: string;
    LeftImage?: ImageField;
    RightImage?: ImageField;
    SplashImage: ImageField;
    Headline: string;
    ComparisonText?: string;
    SplashText: string;
    PlayButtonText: string;
  };
}

async function initializeMediaComparisonSlider(ref: HTMLElement) {
  const { default: MediaComparisonSliderInit } = await import(
    "./MediaComparisonSlider.init.js"
  );
  new MediaComparisonSliderInit(ref);
}

const MediaComparisonSlider: React.FC<MediaComparisonSliderProps> = ({
  data
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [imagesIsLoaded, setImagesIsLoaded] = useState(0);
  const [animation, setAnimation] = useState({});

  const { ref, inView } = useInView({
    threshold: 0.2
  });

  const countLoadedImage = () => {
    setImagesIsLoaded(imagesIsLoaded + 1);
  };

  useEffect(() => {
    if (
      containerRef.current &&
      inView &&
      ((data.LeftVideo && imagesIsLoaded === 1) ||
        (data.LeftImage && data.RightImage && imagesIsLoaded === 3))
    ) {
      initializeMediaComparisonSlider(containerRef.current);

      setAnimation({
        ...animeSingleProps
      });
    }
  }, [imagesIsLoaded, inView, data.LeftImage, data.RightImage, data.LeftVideo]);

  return (
    <div ref={containerRef} className="mediaslider-ribbon">
      <section ref={ref}>
        <Container>
          {data.Headline && (
            <Heading as="h2" className={styles.heading}>
              {data.Headline}
            </Heading>
          )}
          {data.ComparisonText && (
            <Box
              dangerouslySetInnerHTML={{ __html: data.ComparisonText }}
              className={stylesRichtext.richtext}
            />
          )}
          <Anime {...(animation && animation)}>
            <div className="mediaslider-ribbon__outer-wrapper">
              <div className="mediaslider-ribbon__content mediaslider-ribbon__content--with-headline">
                <div className="mediaslider-ribbon__content-inner">
                  {data.LeftVideo ? (
                    <>
                      <div className="mediaslider-ribbon__canvas-wrapper">
                        <canvas
                          className="mediaslider-ribbon__canvas"
                          width="1280"
                          height="720"
                          data-url={data.LeftVideo}
                        ></canvas>
                        <video
                          crossOrigin="anonymous"
                          preload="metadata"
                          playsInline={false}
                          className="hidden"
                          loop={true}
                          onLoad={countLoadedImage.bind(this)}
                        >
                          <source type="video/mp4" src={data.LeftVideo} />
                        </video>
                      </div>
                      <button
                        type="button"
                        className="mediaslider-ribbon__video-control mediaslider-ribbon__video-control--mute"
                        data-action="mute"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 55 47"
                        >
                          <path d="M25.4,5.4v36a2.1,2.1,0,0,1-3.6,1.5l-11-11H2.1A2.1,2.1,0,0,1,0,29.8V17.1A2.1,2.1,0,0,1,2.1,15h8.7l11-11a2.2,2.2,0,0,1,3,0A1.8,1.8,0,0,1,25.4,5.4Z"></path>
                          <path d="M38.1,23.4a8.9,8.9,0,0,1-1.4,4.7A7.4,7.4,0,0,1,33,31.2l-.8.2a2.1,2.1,0,0,1-2.1-2.1,1.6,1.6,0,0,1,.4-1.2,3.9,3.9,0,0,1,1-.8l1.1-.8a4,4,0,0,0,1-1.2,3.6,3.6,0,0,0,.4-1.9,3.6,3.6,0,0,0-.4-1.9,4,4,0,0,0-1-1.2l-1.1-.8a3.9,3.9,0,0,1-1-.8,1.6,1.6,0,0,1-.4-1.2,2.1,2.1,0,0,1,2.1-2.1l.8.2a7.4,7.4,0,0,1,3.7,3.1,8.9,8.9,0,0,1,1.4,4.7"></path>
                          <path d="M46.5,23.4a16.2,16.2,0,0,1-2.8,9.3,16.4,16.4,0,0,1-7.4,6.2l-.8.2A2.1,2.1,0,0,1,33.4,37a2.1,2.1,0,0,1,1.3-2,11.4,11.4,0,0,0,2.5-1.5,12.7,12.7,0,0,0,0-20.4l-2.5-1.5a2,2,0,0,1-1.3-2,2.1,2.1,0,0,1,2.1-2.1l.9.2a16,16,0,0,1,7.4,6.2A17.5,17.5,0,0,1,46.5,23.4Z"></path>
                          <path d="M55,23.4a25,25,0,0,1-4.2,14,24.4,24.4,0,0,1-11.2,9.4l-.9.2a2.1,2.1,0,0,1-2.1-2.1,2.3,2.3,0,0,1,1.3-2l.7-.3.7-.3L42,40.6a20.8,20.8,0,0,0,6.3-7.5,21.2,21.2,0,0,0,0-19.2A20.8,20.8,0,0,0,42,6.4L39.3,4.7l-.7-.3-.7-.3a2.2,2.2,0,0,1-1.3-2A2.1,2.1,0,0,1,38.7,0l.9.2A25,25,0,0,1,50.8,9.6,24.4,24.4,0,0,1,55,23.4Z"></path>
                        </svg>
                      </button>
                      <div className="mediaslider-ribbon__video-controls">
                        <button
                          title="Spol tilbage"
                          type="button"
                          className="mediaslider-ribbon__video-control mediaslider-ribbon__video-control--active mediaslider-ribbon__video-control--rewind"
                          data-action="rewind"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 65 57.1"
                          >
                            <path d="M38.4,11.1V1.9C38.4.1,37.2-.5,35.7.6L1.1,26.6a2.3,2.3,0,0,0,0,4l34.6,26c1.5,1.1,2.7.5,2.7-1.3V46.1L15.1,28.6Z"></path>
                            <path d="M62.3.6l-34.6,26a2.3,2.3,0,0,0,0,4l34.6,26c1.5,1.1,2.7.5,2.7-1.3V2C65,.1,63.8-.5,62.3.6Z"></path>
                          </svg>
                        </button>
                        <button
                          title="Start/stop"
                          type="button"
                          className="mediaslider-ribbon__video-control mediaslider-ribbon__video-control--playpause"
                          data-action="playpause"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 38.4 57.1"
                          >
                            <path d="M2.7,56.6l34.6-26a2.3,2.3,0,0,0,0-4L2.7.6C1.2-.5,0,.1,0,1.9V55.2C0,57.1,1.2,57.7,2.7,56.6Z"></path>
                          </svg>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 46.9 65"
                          >
                            <path d="M14.5,0H3.6A3.6,3.6,0,0,0,0,3.6V61.4A3.6,3.6,0,0,0,3.6,65H14.5a3.6,3.6,0,0,0,3.6-3.6V3.6A3.6,3.6,0,0,0,14.5,0Z"></path>
                            <path d="M43.4,0H32.5a3.6,3.6,0,0,0-3.6,3.6V61.4A3.6,3.6,0,0,0,32.5,65H43.3a3.6,3.6,0,0,0,3.6-3.6V3.6A3.5,3.5,0,0,0,43.4,0Z"></path>
                          </svg>
                        </button>
                        <button
                          title="Gentag automatisk"
                          type="button"
                          className="mediaslider-ribbon__video-control mediaslider-ribbon__video-control--loop mediaslider-ribbon__video-control--active"
                          data-action="loop"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 335.9 448.2"
                          >
                            <path d="M221.2,64.8l-2.4-0.7c-3.7-1-7.5-2-11.8-2.9c-1.7-0.4-3.5-0.7-5.3-1.1c-2.8-0.5-5.6-0.9-8.5-1.2c-2.6-0.4-5.3-0.7-7.9-0.9l-0.1-58L82.3,90l103.3,89.6l-0.2-63.4c0.8,0.1,1.6,0.1,2.3,0.3c2.9,0.4,5.8,1,8.6,1.6c4.1,1,8,2.1,11.8,3.5c2.2,0.8,4.4,1.6,6.5,2.5c4.3,1.9,8.5,4.1,12.6,6.6l1.2,0.7c1,0.6,2,1.2,3,1.8c4.3,2.9,8.4,6.2,12.5,10l1.1,0.9c0.5,0.4,0.9,0.8,1.3,1.1c4,4,7.7,8.3,11.1,13.1l0.7,0.8c0.3,0.3,0.5,0.6,0.8,1c2.9,4.2,5.7,9.1,8.4,14.9c0.2,0.4,0.4,0.8,0.6,1.2c0.2,0.3,0.4,0.7,0.5,1.1c2,4.5,3.6,9.6,5.2,16c0.1,0.5,0.3,1.1,0.5,1.6c0.1,0.4,0.3,0.8,0.4,1.4c1.4,6.5,2.1,13.3,2.1,20.2c0.1,21.8-6.9,42.7-20.3,60.3l-5.5,7.2l43.4,39.2l7-8.6c22.8-28.4,34.8-62.3,34.7-98.3C335.9,146.4,289.7,85.5,221.2,64.8z M150.8,331.9c-0.8-0.1-1.6-0.1-2.4-0.3c-2.9-0.4-5.8-1-8.6-1.6c-3.9-0.9-7.8-2.1-11.8-3.5c-2.2-0.8-4.4-1.6-6.5-2.5c-4.2-1.9-8.4-4.1-12.6-6.6l-1.4-0.8c-0.9-0.6-1.9-1.1-2.8-1.7c-4.3-2.9-8.4-6.2-12.5-10l-1.1-0.9c-0.5-0.4-0.9-0.8-1.3-1.1c-3.9-3.8-7.5-8.1-11.1-13c-0.2-0.3-0.5-0.7-0.8-1c-0.2-0.3-0.5-0.5-0.7-0.9c-3-4.3-5.7-9.2-8.4-14.9c-0.2-0.5-0.5-1-0.7-1.4c-0.2-0.3-0.4-0.6-0.5-0.9c-1.9-4.4-3.6-9.4-5.2-16c-0.1-0.6-0.3-1.1-0.5-1.6c-0.1-0.4-0.3-0.8-0.4-1.4c-1.4-6.5-2.1-13.2-2.2-20.1c-0.1-21.9,7-42.7,20.3-60.3l5.5-7.2L41.7,125l-7,8.6C11.9,161.9-0.1,195.9,0,231.9c0.1,69.9,46.3,130.8,114.9,151.5l2.4,0.7c3.7,1,7.5,2,11.8,2.9c1.7,0.4,3.5,0.8,5.3,1.1c2.8,0.5,5.7,0.9,8.5,1.2c2.7,0.4,5.3,0.6,7.9,0.9l0.1,58l102.9-90l-103.3-89.6L150.8,331.9z"></path>
                          </svg>
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="mediaslider-ribbon__primary">
                        {data.LeftImage && (
                          <UmbracoImage
                            image={data.LeftImage}
                            width={[335, 688, 944, 1320]}
                            className="mediaslider-ribbon__image"
                            onLoad={countLoadedImage.bind(this)}
                            isCover
                          />
                        )}
                      </div>
                      <div className="mediaslider-ribbon__secondary">
                        <div className="mediaslider-ribbon__secondary-inner">
                          {data.RightImage && (
                            <UmbracoImage
                              image={data.RightImage}
                              className="mediaslider-ribbon__image"
                              onLoad={countLoadedImage.bind(this)}
                              isCover
                            />
                          )}
                        </div>
                      </div>
                    </>
                  )}

                  <button type="button" className="mediaslider-ribbon__divider">
                    <span className=""></span>
                  </button>
                </div>
                <div className="mediaslider-ribbon__splash">
                  {data.SplashImage && (
                    <UmbracoBackgroundImage
                      image={data.SplashImage}
                      onLoad={countLoadedImage.bind(this)}
                    >
                      <div className="mediaslider-ribbon__splash-content">
                        {data.SplashText && (
                          <div
                            className="mediaslider-ribbon__splash-text"
                            dangerouslySetInnerHTML={{
                              __html: data.SplashText
                            }}
                          />
                        )}
                        <button
                          type="button"
                          className="mediaslider-ribbon__start-button"
                        >
                          {data.PlayButtonText}
                        </button>
                      </div>
                    </UmbracoBackgroundImage>
                  )}
                </div>
              </div>
            </div>
          </Anime>
        </Container>
      </section>
    </div>
  );
};

export { MediaComparisonSlider };
